import { useState } from "react";
import { useBluelagoon } from "@bluelagoon";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const DEFAULT_COLOR = "#3b82f6";

export const DialogCreateGroup = ({ onCreated }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [color, setColor] = useState(DEFAULT_COLOR);

    const { call: createGroup, isLoading } = useBluelagoon({
        method: "createGroup",
    });

    const reset = () => {
        setName("");
        setDescription("");
        setColor(DEFAULT_COLOR);
    };

    const handleCreate = async () => {
        if (!name.trim()) {
            toast.error(t("groups.groupNameRequired"));
            return;
        }
        try {
            await createGroup({
                name,
                description: description || undefined,
                color,
            });
            toast.success(t("groups.groupCreated"));
            reset();
            setOpen(false);
            onCreated?.();
        } catch (err) {
            console.error("Failed to create group:", err);
            toast.error(err?.message || t("groups.failedToCreateGroup"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    {t("groups.createGroup")}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t("groups.createNewGroup")}</DialogTitle>
                    <DialogDescription>
                        {t("groups.createDescription")}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="group-name" className="text-right">
                            {t("groups.name")}
                        </Label>
                        <Input
                            id="group-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="col-span-3"
                            placeholder={t("groups.enterName")}
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label
                            htmlFor="group-description"
                            className="text-right"
                        >
                            {t("groups.description")}
                        </Label>
                        <Textarea
                            id="group-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="col-span-3"
                            placeholder={t("groups.enterDescription")}
                        />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="group-color" className="text-right">
                            {t("groups.color")}
                        </Label>
                        <Input
                            id="group-color"
                            type="color"
                            value={color}
                            onChange={(e) => setColor(e.target.value)}
                            className="col-span-3 h-10"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        onClick={handleCreate}
                        disabled={isLoading || !name.trim()}
                    >
                        {isLoading
                            ? t("groups.creating")
                            : t("groups.createGroup")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default DialogCreateGroup;
